import { faCircleExclamation, faDownload, faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popper } from "@mui/material";
import TooltipMUI from '@mui/material/Tooltip';
import { css, styled } from '@mui/system';
import { process } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Grid, GridColumn, GridColumnMenuFilter, GridColumnMenuGroup, GridColumnMenuSort, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoaderOverlay from '../../components/custom-loader/LoaderOverlay';
import { applyFilter, applySort, flatternGroup, formatDate, getTodayDate } from '../../custom/commonExport';
import { validateUserData } from '../../custom/UserExport';
import { encryptData } from '../../services/dashboard-service/encryptionService';
import { getUserDownloadHisotry } from '../../services/dashboard-service/userService';

const blue = {
  50: '#F0F7FF',
  100: '#C2E0FF',
  200: '#99CCF3',
  300: '#66B2FF',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  700: '#0059B2',
  800: '#004C99',
  900: '#003A75',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};


const initialDataState = {
  take: 100, // Number of items per page
  skip: 0,  // Current offset
  sort: [],
  filter: null,
  group: [],
};

const regions = [
  { id: 1, name: 'Finland', value: "fi" },
  { id: 2, name: 'Sweden', value: "se" },
  { id: 3, name: 'Denmark', value: "dk" },
  { id: 4, name: 'Estonia', value: "et" },
  { id: 5, name: 'France', value: 'fr' },
  { id: 6, name: 'Italy', value: 'it' },
  { id: 7, name: 'Norway', value: 'no' },
  { id: 8, name: 'Spain', value: 'sp' },
  { id: 9, name: 'UK', value: 'uk' },
]

const processWithGroups = (data, dataState) => {
  const newDataState = process(data, dataState);
  setGroupIds({
    data: newDataState.data,
    group: dataState.group
  });
  return newDataState;
};


const mappingFields = {
  "FirstName": {
    "value": "First Name",
    "width": "200px"
  },
  "Email": {
    "value": "Email",
    "width": "200px"
  },
  "region": {
    "value": "Region",
    "width": "200px"
  },
  "year": {
    "value": "Year",
    "width": "200px"
  },
  "month": {
    "value": "Month",
    "width": "250px"
  },
  "type_export": {
    "value": " Type",
    "width": "200px"
  },
  "file_type": {
    "value": "File Type",
    "width": "200px"
  },
  "createdAt": {
    "value": "Download Date",
    "width": "200px"
  }
}

function HistoryPage() {
  const [data, setData] = useState([])
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false)
  const excelExportRef = useRef(null);
  const [collapsedState, setCollapsedState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('')
  const [filteredData, setFilteredData] = useState([]);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [dataState, setDataState] = useState(initialDataState);
  const [historyData, setHistoryData] = useState(processWithGroups(filteredData, initialDataState));
  const [anchorEl, setAnchorEl] = useState(null);
  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const userData = validateUserData()
  const navigate = useNavigate()
  const currentDate = getTodayDate()
  if (!userData) {
    navigate("/login")
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const changeLang = (lang) => {
    i18n.changeLanguage(lang.value);
    const encryptedLang = encryptData(JSON.stringify(lang.value));
    localStorage.setItem("CurrentLanguage", encryptedLang);
    const region = regions.find(r => r.value === lang.value);
  }


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) {
        setShow(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    };
  }, [])

  const handlePopperToggle = (event) => {
    setShow((prev) => !prev);
    anchorRef.current = event.currentTarget;
  };



  function getRegionName(name) {
    const regionName = regions.find((item) => item.value === name)
    return regionName?.name || "unknown"
  }

  const onExpandChange = useCallback(event => {
    const item = event.dataItem;
    if (item.groupId) {
      const collapsedIds = !event.value ? [...collapsedState, item.groupId] : collapsedState.filter(groupId => groupId !== item.groupId);
      setCollapsedState(collapsedIds);
    }
  }, [collapsedState]);


  const newData = setExpandedState({
    data: historyData.data || [],
    collapsedIds: collapsedState
  });



  const exportExcel = async () => {
    setLoading(true)
    const dataStateForExport = {
      ...dataState,
      skip: 0,
      take: Number.MAX_SAFE_INTEGER,
    };
    let processedData = filteredData
    if (dataStateForExport?.filter?.filters.length > 0) {
      processedData = applyFilter(filteredData, dataStateForExport?.filter.filters, dataStateForExport?.filter.filters.logic)
    }

    if (dataStateForExport.group.length > 0) {
      const filteredAndGroupedData = process(processedData, dataStateForExport);
      processedData = dataState.group && dataState.group.length > 0 ? flatternGroup(filteredAndGroupedData.data) : filteredData
    }
    if (dataStateForExport?.sort?.length > 0) {
      processedData = applySort(processedData, dataStateForExport.sort);
    }

    if (processedData.length === 0) {
      toast("No data available for export", { type: "warning", autoClose: true });
      setLoading(false);
      return;
    }


    if (excelExportRef.current) {
      try {

        // Trigger the export
        const options = {
          data: processedData,
          fileName: `History_${currentDate}.xlsx`,
          creator: 'Vioservice',
          title: 'History Export',
          filterable: true,
          columns
        };

        await excelExportRef.current.save(options);
        // Ensure loading state is cleared after the export is triggered

      } catch (error) {
        console.error("Excel export failed:", error);
        toast("Failed to export data to Excel.", { type: "error", autoClose: true });
      }
    }
    else {
      setLoading(false)
    }
  }
  const fetchData = async () => {
    if (historyData.length > 0) return;
    if (userData && !loading) {
      setLoading(true)
      const getHistoryDataResponse = await getUserDownloadHisotry(userData?.SecretKey, userData?.Token)
      if (getHistoryDataResponse?.status === 200) {
        const transformedData = getHistoryDataResponse?.data.map((item) => ({
          ...item,
          createdAt: formatDate(new Date(item?.createdAt)),
          type_export: item?.type_export === 0 ? "Vehicle VIO Export" : "Article VIO Export",
          file_type: item?.file_type === 0 ? "Excel" : "CSV",
          region: getRegionName(item?.region)
        }))
        setData(transformedData)
        const processedData = processWithGroups(transformedData, initialDataState)
        setIsDataAvailable(transformedData.length > 0);
        setFilteredData(transformedData)
        setHistoryData(processedData)
        setLoading(false);
        return;
      }
      else if (getHistoryDataResponse?.status === 401 || getHistoryDataResponse?.error?.statusCode === 401) {
        setLoading(false)
        localStorage.removeItem("userData")
        toast.error('Session Expired.')
        navigate("/login")
      }
      else {
        setHistoryData([])
        setLoading(false)
      }
    }
    setLoading(false)
  }
  useEffect(() => {
    fetchData()
  }, [])


  const colunForROOT = [
    { field: 'FirstName', title: t("FirstName") },
    { field: 'Email', title: t("Email") },
    { field: 'region', title: t("Region") },
    { field: 'year', title: t("Year") },
    { field: 'month', title: t("Month") },
    { field: 'type_export', title: t("Type") },
    { field: 'file_type', title: t("File") },
    { field: 'createdAt', title: t("DownloadDate") }
  ]
  const columnForUser = [
    { field: 'FirstName', title: t("FirstName") },
    { field: 'Email', title: t("Email") },
    { field: 'region', title: t("Region") },
    { field: 'year', title: t("Year") },
    { field: 'month', title: t("Month") },
    { field: 'type_export', title: t("Type") },
    { field: 'file_type', title: t("File") },
    { field: 'createdAt', title: t("DownloadDate") }
  ]
  const columns = colunForROOT




  const onFilterChange = (e) => {
    const value = e.target.value.toLowerCase();
    setFilterValue(value);
    const filteredData = data.filter(item => {
      return Object.values(item).some(property => {
        return property !== null && property.toString().toLowerCase().includes(value)
      })
    })
    setDataState({
      ...dataState,
      skip: 0, // Reset to the first page when filter is applied
    });

    // Process the filtered data
    const processedData = process(filteredData, { ...dataState, skip: 0 });
    setFilteredData(filteredData);
    setHistoryData(processedData);
  }

  const dataStateChange = (event) => {
    const updatedState = event.dataState;
    setDataState(updatedState);
    setHistoryData(processWithGroups(filteredData, event.dataState))
  };


  const exportComplete = () => {
    setLoading(false)
    toast("Excel Exported Successfully", { type: "success", autoClose: true });
  }



  const handleExportCsv = async () => {
    try {
      setLoading(true)
      const headers = Object.keys(mappingFields).map((key) => ({
        label: mappingFields[key].value,
        key: key,
      }));


      const dataStateForExport = {
        ...dataState,
        skip: 0,
        take: Number.MAX_SAFE_INTEGER,
      };

      let processedData = filteredData
      if (dataStateForExport?.filter?.filters.length > 0) {
        processedData = applyFilter(filteredData, dataStateForExport?.filter.filters, dataStateForExport?.filter.filters.logic)
      }

      if (dataStateForExport.group.length > 0) {
        const filteredAndGroupedData = process(processedData, dataStateForExport);
        processedData = dataState.group && dataState.group.length > 0 ? flatternGroup(filteredAndGroupedData.data) : filteredData
      }
      if (dataStateForExport?.sort?.length > 0) {
        processedData = applySort(processedData, dataStateForExport.sort);
      }


      if (processedData.length === 0) {
        toast("No data available for export", { type: "warning", autoClose: true });
        setLoading(false);
        return;
      }


      const csvData = processedData.map(item => {
        return Object.keys(mappingFields).reduce((acc, key) => {
          let value = item[key] != null ? item[key].toString() : '';
          // Escape double quotes inside the data by replacing " with ""
          value = value.replace(/"/g, '""');
          // Enclose values in double quotes (only for strings and non-numeric values)
          acc[key] = value ? `"${value}"` : ''; // Add quotes to non-empty values
          return acc;
        }, {});
      });

      // Generate CSV string with quotes around header labels and data rows
      const csvString = [
        // Header row with quotes around the label
        headers.map(header => `"${header.label.replace(/"/g, '""')}"`).join(','),
        // Data rows
        ...csvData.map(row => headers.map(header => row[header.key] || '').join(','))
      ].join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `History_${currentDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setLoading(false);
    } catch (error) {
      setLoading(false)
      console.error('Error exporting CSV:', error);
    }
  };





  return (
    <div className='userlist-main-div' style={{ height: "100%", position: 'relative' }}>
      <LoaderOverlay loading={loading} />
      <div className='userlist-grid' style={{ height: "100%" }}>
        <ExcelExport data={filteredData} ref={excelExportRef} fileName={`History_${currentDate}.xlsx`} onExportComplete={exportComplete}>
          <Grid data={newData}
            pageable={isDataAvailable ? {
              buttonCount: 4,
              pageSizes: [100, 200, 500, 1000],
            } : false}
            groupable={isDataAvailable}
            sortable={isDataAvailable}
            total={historyData.total}
            group={dataState?.group}
            onDataStateChange={dataStateChange}
            onExpandChange={onExpandChange}
            expandField="expanded"
            onExportComplete={exportComplete}
            {...dataState}>
            <GridToolbar>
              <div className='header-div'>
                <div className='input-filters'>
                  <Input
                    value={filterValue}
                    disabled={!isDataAvailable}
                    onChange={(e) => onFilterChange(e)}
                    style={{ border: '2px solid #ccc', width: '210px', height: '30px', marginRight: '10px' }}
                    placeholder="Search in all columns..."
                  />
                </div>
                <div className="export-btns-container" style={{ position: 'relative' }}>
                  <TooltipMUI title={isDataAvailable ? "Download" : "No data available"}>
                    <span>

                      <TriggerButton style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: isDataAvailable ? 'pointer' : 'not-allowed',
                        color: isDataAvailable ? "rgb(83, 83, 83)" : "rgb(189, 189, 189)"
                      }} aria-describedby={id} type="button" onClick={handlePopperToggle} disabled={!isDataAvailable}>
                        <FontAwesomeIcon icon={faDownload} size="xl" />
                      </TriggerButton>
                    </span>
                  </TooltipMUI>
                  <Popper open={show} anchorEl={anchorRef.current} ref={popupRef} sx={{ top: "0px !important", left: "-10px !important", zIndex: 100000 }} onClose={() => setShow(false)}>
                    <StyledPopperDiv>
                      <Button onClick={handleExportCsv}
                        style={{ margin: '5px 0px' }} className="forFileExcel">
                        <FontAwesomeIcon icon={faFileCsv} />
                      </Button>
                      <Button onClick={exportExcel} style={{ margin: '5px 0px' }} className="forFileExcel">
                        <FontAwesomeIcon icon={faFileExcel} />
                      </Button>
                    </StyledPopperDiv>
                  </Popper>
                </div>
              </div>
            </GridToolbar>
            <GridNoRecords>
              <div className="no-data-found">
                <div className="no-data-icon">
                  <FontAwesomeIcon icon={faCircleExclamation} size="7x" />
                </div>
                <div className="no-data-message">
                  No Data Available
                </div>
              </div>
            </GridNoRecords>
            {
              columns.map((col) => (
                <GridColumn
                  key={col.field} // Use field as the unique key
                  field={col.field}
                  title={col.title}
                  flex={1}
                  columnMenu={isDataAvailable ? ColumnMenu : null}
                />
              ))

            }
          </Grid>
        </ExcelExport>
      </div>
    </div >
  )
}

const ColumnMenu = (props) => (
  <div>
    <GridColumnMenuSort {...props} />
    <GridColumnMenuFilter {...props} />
    <GridColumnMenuGroup {...props} />
  </div>
);

const TriggerButton = styled('button')(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 5px 5px;
  background-color: transparent;
  border-radius: 8px;
  color: grey[500];
  transition: all 150ms ease;
  cursor: pointer;
  border: none;
  box-shadow: none;

  &:focus-visible {
    box-shadow: 0 0 0 4px ${theme.palette.mode === 'dark' ? grey[300] : grey[200]};
    outline: none;
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      background-color: ${grey[500]};
    }
  }
`
);

const StyledPopperDiv = styled('div')(
  ({ theme }) => css`
    background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
    border-radius: 8px;
    border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    box-shadow: ${theme.palette.mode === 'dark'
      ? `0px 4px 8px rgb(0 0 0 / 0.7)`
      : `0px 4px 8px rgb(0 0 0 / 0.1)`};
    padding: 10px;
    color: ${theme.palette.mode === 'dark' ? grey[100] : grey[700]};
    font-size: 0.875rem;
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 500;
    opacity: 1;
    margin: 0.25rem 0;
  `
);




export default HistoryPage